import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type OrderForApproval,
  type ApproveOrdersForInvoicingRequest,
  type ApproveOrdersForInvoicingResult,
} from '../client';

/** Query keys for the For-approval queue — scoped per organisation. */
export const ordersForApprovalKeys = {
  all: ['tms', 'ordersForApproval'] as const,
  byOrg: (organisationId: string) => ['tms', 'ordersForApproval', 'org', organisationId] as const,
};

/**
 * Orders awaiting approval-for-invoicing for an org (Completed + Pending), with
 * charge totals and live approval exceptions. Pass `null` to keep it disabled
 * until the org id is known.
 */
export function useOrdersForApproval(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<OrderForApproval[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderForApproval[], Error>({
    queryKey: organisationId
      ? ordersForApprovalKeys.byOrg(organisationId)
      : ordersForApprovalKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getOrdersForApproval(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Approve one or more orders for invoicing. Invalidates the org's queue so the
 * approved rows drop out (and any server-rejected ones reappear with reasons).
 */
export function useApproveOrdersForInvoicing() {
  const qc = useQueryClient();
  return useMutation<ApproveOrdersForInvoicingResult, Error, ApproveOrdersForInvoicingRequest>({
    mutationKey: [...ordersForApprovalKeys.all, 'approve'],
    mutationFn: (body) => tmsApi.approveOrdersForInvoicing(body),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: ordersForApprovalKeys.byOrg(organisationId) });
    },
  });
}
